import { useState, useCallback, useEffect } from 'react';
import type { Language } from '@sourcr/shared';

type Theme = 'dark' | 'light';
type Density = 'compact' | 'regular' | 'comfy';

const STORAGE_KEY = 'sourcr:app-state';

interface PersistedState {
  lang: Language;
  theme: Theme;
  density: Density;
}

// UA audience — Ukrainian by default.
const DEFAULTS: PersistedState = { lang: 'uk', theme: 'dark', density: 'regular' };

function loadPersisted(): PersistedState {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const p = JSON.parse(raw) as Partial<PersistedState>;
    return {
      lang: p.lang === 'en' || p.lang === 'uk' ? p.lang : DEFAULTS.lang,
      theme: p.theme === 'light' || p.theme === 'dark' ? p.theme : DEFAULTS.theme,
      density:
        p.density === 'compact' || p.density === 'regular' || p.density === 'comfy'
          ? p.density
          : DEFAULTS.density,
    };
  } catch {
    return DEFAULTS;
  }
}

interface AppState {
  lang: Language;
  theme: Theme;
  density: Density;
}

interface AppStateActions {
  setLang: (lang: Language) => void;
  setTheme: (theme: Theme) => void;
  setDensity: (density: Density) => void;
  toggleTheme: () => void;
  toggleLang: () => void;
}

export function useAppState(): AppState & AppStateActions {
  const [lang, setLang] = useState<Language>(() => loadPersisted().lang);
  const [theme, setTheme] = useState<Theme>(() => loadPersisted().theme);
  const [density, setDensity] = useState<Density>(() => loadPersisted().density);

  // Persist any change.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ lang, theme, density }));
    } catch {
      // ignore quota / private mode
    }
  }, [lang, theme, density]);

  // Keep <html lang> in sync (a11y + SEO).
  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const toggleTheme = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);
  const toggleLang = useCallback(() => setLang((l) => (l === 'en' ? 'uk' : 'en')), []);

  return { lang, theme, density, setLang, setTheme, setDensity, toggleTheme, toggleLang };
}
